import {
  validateCriterionScoringBandSet,
  type CustomerValueCriterionDefinition,
  type CustomerValueCriterionScoringBand,
  type CustomerValueModelType,
  type CustomerValueModelStorageType
} from "./criterionScoring";

export type CustomerValueModelReadinessStatus =
  | "not_configured"
  | "in_progress"
  | "scoring_incomplete"
  | "ready_for_review"
  | "preview_ready"
  | "confirmed";

export interface CustomerValueModelReadinessInput {
  modelStatus: string | null | undefined;
  modelType: CustomerValueModelStorageType | null | undefined;
  activeWeightTotal: number;
  segmentStatus?: string | null;
  criteria: readonly CustomerValueCriterionDefinition[];
  bandsByCriterionId: Record<string, readonly CustomerValueCriterionScoringBand[]>;
}

export interface CustomerValueModelReadiness {
  status: CustomerValueModelReadinessStatus;
  modelTypeConfigured: boolean;
  modelVersionConfirmed: boolean;
  weightsComplete: boolean;
  segmentsConfirmed: boolean;
  scoringComplete: boolean;
  scoringPromotionReady: boolean;
  confirmationReady: boolean;
  previewReady: boolean;
  blockingCriterionIds: string[];
  promotionBlockingCriterionIds: string[];
}

const EPSILON = 1e-9;

export function hasConfiguredCustomerValueModelType(modelType: CustomerValueModelStorageType | null | undefined): modelType is CustomerValueModelType {
  return modelType === "existing_customer_value" || modelType === "new_customer_value";
}

function activeConfirmedBands(bands: readonly CustomerValueCriterionScoringBand[]): CustomerValueCriterionScoringBand[] {
  return bands.filter((band) => band.active && band.status === "confirmed");
}

function activeDraftBands(bands: readonly CustomerValueCriterionScoringBand[]): CustomerValueCriterionScoringBand[] {
  return bands.filter((band) => band.active && band.status === "draft");
}

function hasActiveScoringConfiguration(bands: readonly CustomerValueCriterionScoringBand[]): boolean {
  return bands.some((band) => band.active && (band.status === "draft" || band.status === "confirmed"));
}

export function isConfirmedLegacyAssessmentCriterionWithoutScoringBands(
  criterion: CustomerValueCriterionDefinition,
  bands: readonly CustomerValueCriterionScoringBand[],
  modelStatus: string | null | undefined
): boolean {
  return modelStatus === "confirmed" &&
    criterion.active &&
    criterion.criterionType === "legacy" &&
    criterion.sourceStrategy === "legacy_assessment" &&
    !hasActiveScoringConfiguration(bands);
}

export function hasCompleteConfirmedCriterionScoring(
  criterion: CustomerValueCriterionDefinition,
  bands: readonly CustomerValueCriterionScoringBand[],
  context: { modelStatus?: string | null } = {}
): boolean {
  if (!criterion.active) return true;
  if (isConfirmedLegacyAssessmentCriterionWithoutScoringBands(criterion, bands, context.modelStatus)) return true;
  if (criterion.scoringConfigurationStatus !== "confirmed") return false;
  const confirmedBands = activeConfirmedBands(bands);
  if (confirmedBands.length === 0) return false;
  return validateCriterionScoringBandSet(criterion, confirmedBands).previewEligible;
}

export function hasPromotableCriterionScoring(
  criterion: CustomerValueCriterionDefinition,
  bands: readonly CustomerValueCriterionScoringBand[],
  context: { modelStatus?: string | null } = {}
): boolean {
  if (!criterion.active) return true;
  if (hasCompleteConfirmedCriterionScoring(criterion, bands, context)) return true;
  if (criterion.scoringConfigurationStatus !== "draft") return false;
  const draftBands = activeDraftBands(bands);
  if (draftBands.length === 0) return false;
  return validateCriterionScoringBandSet(criterion, draftBands).confirmationAllowed;
}

export function resolveCustomerValueModelReadiness(input: CustomerValueModelReadinessInput): CustomerValueModelReadiness {
  const modelTypeConfigured = hasConfiguredCustomerValueModelType(input.modelType);
  const modelVersionConfirmed = input.modelStatus === "confirmed";
  const weightsComplete = Math.abs(input.activeWeightTotal - 100) <= EPSILON;
  const segmentsConfirmed = input.segmentStatus === "confirmed";
  const blockingCriterionIds = input.criteria
    .filter((criterion) => criterion.active)
    .filter((criterion) => !hasCompleteConfirmedCriterionScoring(criterion, input.bandsByCriterionId[criterion.id] ?? [], { modelStatus: input.modelStatus }))
    .map((criterion) => criterion.id);
  const promotionBlockingCriterionIds = input.criteria
    .filter((criterion) => criterion.active)
    .filter((criterion) => !hasPromotableCriterionScoring(criterion, input.bandsByCriterionId[criterion.id] ?? [], { modelStatus: input.modelStatus }))
    .map((criterion) => criterion.id);
  const scoringComplete = blockingCriterionIds.length === 0;
  const scoringPromotionReady = promotionBlockingCriterionIds.length === 0;
  const confirmationReady = modelTypeConfigured && weightsComplete && segmentsConfirmed && scoringPromotionReady;
  const previewReady = modelTypeConfigured && weightsComplete && segmentsConfirmed && scoringComplete;
  let status: CustomerValueModelReadinessStatus = "not_configured";
  if (!modelTypeConfigured) {
    status = "not_configured";
  } else if (!weightsComplete) {
    status = "in_progress";
  } else if (!scoringComplete) {
    status = "scoring_incomplete";
  } else if (!segmentsConfirmed) {
    status = "ready_for_review";
  } else if (modelVersionConfirmed) {
    status = "confirmed";
  } else {
    status = "preview_ready";
  }
  return {
    status,
    modelTypeConfigured,
    modelVersionConfirmed,
    weightsComplete,
    segmentsConfirmed,
    scoringComplete,
    scoringPromotionReady,
    confirmationReady,
    previewReady,
    blockingCriterionIds,
    promotionBlockingCriterionIds
  };
}
